'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { Input } from '@/components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCommon } from '@/contexts/common-context';
import { useToast } from '@/hooks/use-toast';
import { useFilterStore } from '@/stores/filter-store';
import type { Car, DealerDto, SalesRow, StatementSummary } from '@/lib/api/data-contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';
import { DateRangeInputs } from './date-range-inputs';
import { checkedRows, GRID_BASE } from './grid-shared';
import { buildInvoiceDocument, type OilMaster } from './invoice-build-doc';
import {
  ISSUE_TERM_AD_HOC,
  ISSUE_TERM_LABEL,
  currentMonthRange,
} from './issue-period';
import {
  cancelStatements,
  fetchCars,
  fetchDealers,
  fetchInvoiceDocuments,
  fetchSalesRows,
  fetchStatements,
  generateStatements,
  generateTaxInvoices,
  toggleReceiptPurpose,
  updateTaxInvoiceRemark,
} from './invoice-data-source';
import type { InvoiceDocumentDto } from './invoice-document-types';
import { InvoicePreviewOverlay } from './invoice-preview-overlay';
import { PrintPreviewOverlay } from '@/components/common/print-preview-overlay';
import { buildTaxInvoiceDocument } from '@/components/features/sales/tax-invoice/tax-invoice-build-doc';
import { fetchTaxInvoiceDocument } from '@/components/features/sales/tax-invoice/tax-invoice-data-source';
import { TaxInvoiceDocument } from '@/components/features/sales/tax-invoice/tax-invoice-document';
import type { TaxInvoiceDocumentDto } from '@/components/features/sales/tax-invoice/tax-invoice-document-types';

// toISOString()은 UTC 기준이라 KST 매월 1일 09시 이전에 전월을 반환한다.
function toIssueYm(startDate: string) {
  return startDate.slice(0, 7).replace('-', '');
}

/** Radix Select 는 빈 문자열 값을 허용하지 않아 "선택 안 함"을 이 값으로 나타낸다 */
const NONE = '__none__';

export function Invoice() {
  const { toast } = useToast();
    const { getOils } = useCommon();

  const initialRange = useMemo(currentMonthRange, []);
  const [issueTerm, setIssueTerm] = useState('1');
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [adHocDealCode, setAdHocDealCode] = useState('');
  const [adHocGeoEcode, setAdHocGeoEcode] = useState('');
  const [adHocOilType, setAdHocOilType] = useState('');

  /** 검색조건 거래처·확장코드 선택지(정본 DEALERS·CARS) - 선택한 주유소 기준으로 읽는다 */
  const [custList, setCustList] = useState<DealerDto[]>([]);
  const [custOpen, setCustOpen] = useState(false);
  const [cars, setCars] = useState<Car[]>([]);
  const [salesRows, setSalesRows] = useState<SalesRow[]>([]);
  const [statements, setStatements] = useState<StatementSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewDocs, setPreviewDocs] = useState<InvoiceDocumentDto[] | null>(null);
  const [taxPreviewDoc, setTaxPreviewDoc] = useState<TaxInvoiceDocumentDto | null>(null);
  const [taxRemarkDraft, setTaxRemarkDraft] = useState('');

  const isAdHoc = issueTerm === ISSUE_TERM_AD_HOC;


  // 필터 변경에 반응해야 안내가 즉시 사라진다. getState() 로 읽으면 다음 액션까지 남아 있다.
  const selectedStore = useFilterStore(state => state.store[0] ?? '');
  const storeNotice = selectedStore
    ? ''
    : '상단 필터에서 주유소를 선택하면 조회할 수 있습니다.';

  useEffect(() => {
    // 주유소를 바꾸면 이전 주유소의 조회 결과·수시 조건이 남으면 안 된다 - 유종·거래처 코드는 주유소마다 다르다
    setSalesRows([]);
    setStatements([]);
    setAdHocDealCode('');
    setAdHocGeoEcode('');
    setAdHocOilType('');
    (selectedStore
      ? Promise.all([fetchDealers(selectedStore), fetchCars(selectedStore)]).catch(() => [[], []])
      : Promise.resolve([[], []])
    ).then(([dealerList, carList]) => {
      setCustList(dealerList);
      setCars(carList);
    });
  }, [selectedStore]);

  // 거래처 선택은 코드 순으로 고정한다. 서버는 정렬을 보장하지 않는다.
  const sortedCust = useMemo(
    () => [...custList].sort((a, b) => (a.dealCode ?? '').localeCompare(b.dealCode ?? '')),
    [custList]
  );
  const selectedCust = custList.find(d => d.dealCode === adHocDealCode);
  const custLabel = (d: DealerDto) =>
    `${d.dealCode} ${d.dealName}${d.invoiceIssueTerm === '0' ? ' (미발행등록)' : ''}`;

  // 확장코드는 거래처가 use_geo_ecode=1이고 확장코드가 등록된 차량이 있을 때만 고를 수 있다(§2.7.2)
  const geoOptions =
    custList.find(d => d.dealCode === adHocDealCode)?.useGeoEcode === '1'
      ? [...new Set(cars.filter(c => c.dealCode === adHocDealCode && c.geoEcode).map(c => c.geoEcode as string))]
      : [];

  const storeCd = useCallback(() => {
    // 발행 API 는 발행부서 1곳만 받는다. 임의로 한 곳을 골라 조회하면 사용자가 다른 주유소를
    // 보고 있다고 착각하므로, 선택하기 전에는 조회하지 않는다(상단 필터도 단일 선택으로 제한).
    return useFilterStore.getState().store[0] ?? '';
  }, []);

  /**
   * 주유소가 선택돼 있으면 그 코드를, 아니면 null 을 준다.
   *
   * 조회뿐 아니라 생성·취소·대사 같은 파괴적 동작도 주유소 없이 나가면 안 된다.
   * 빈 storeCd 로 서버에 도달하는 경로를 만들지 않기 위해 모든 핸들러가 이걸 쓴다.
   */
  const requireStore = useCallback(() => {
    const store = storeCd();
    if (!store) {
      toast({ title: '주유소를 선택하세요', variant: 'destructive' });
      return null;
    }
    // 수시는 발행기간이 원시 date 입력이라 비워둘 수 있다. 그대로 두면 issueYm 이 빈 값으로 나가
    // 서버 400 으로만 알게 된다(서버가 YYYYMM 을 강제하므로 잘못 삭제되지는 않는다).
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      toast({ title: '발행기간을 입력하세요', variant: 'destructive' });
      return null;
    }
    return store;
  }, [endDate, startDate, storeCd, toast]);

  const handleExpandChange = useCallback((expanded: boolean) => {
    setIsExpanded(expanded);
  }, []);

  const fail = useCallback(
    (title: string, error: unknown) => {
      toast({
        title,
        description: error instanceof Error ? error.message : '요청을 처리하지 못했습니다',
        variant: 'destructive',
      });
    },
    [toast]
  );

  const handleSearch = useCallback(async () => {
    const store = requireStore();
    if (!store) return;
    if (isAdHoc && !adHocDealCode) {
      toast({ title: '수시 발행은 거래처를 지정해야 합니다', variant: 'destructive' });
      return;
    }

    setIsLoading(true);
    try {
      const dealCode = isAdHoc ? adHocDealCode : undefined;
      const [sales, summaries] = await Promise.all([
        fetchSalesRows({
          storeCd: store,
          issueTerm,
          startDate,
          endDate,
          dealCode,
          geoEcode: isAdHoc ? adHocGeoEcode : undefined,
          oilType: isAdHoc ? adHocOilType : undefined,
        }),
        fetchStatements({ storeCd: store, issueYm: toIssueYm(startDate) }),
      ]);
      setSalesRows(sales);
      setStatements(summaries);
    } catch (error) {
      fail('조회 실패', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    adHocDealCode,
    adHocGeoEcode,
    adHocOilType,
    endDate,
    fail,
    isAdHoc,
    issueTerm,
    startDate,
    storeCd,
    toast,
  ]);

  /** 거래명세서 생성 - 거래내역 그리드에서 체크한 행(pos_mchul.no)만 확정한다 */
  const handleGenerate = useCallback(async (srcNos: number[]) => {
    const store = requireStore();
    if (!store) return;
    // 수시는 거래처 1건이 대상이므로 미지정이면 빈 거래처로 생성 요청이 나간다
    if (isAdHoc && !adHocDealCode) {
      toast({ title: '수시 발행은 거래처를 지정해야 합니다', variant: 'destructive' });
      return;
    }
    if (srcNos.length === 0) {
      toast({ title: '확정할 거래내역을 선택하세요', variant: 'destructive' });
      return;
    }
    try {
      // 선택한 미확정 행만 확정한다. 이미 확정된 행은 서버가 항상 제외하므로 같은 달에 추가 확정할 수 있다(§4.8)
      const result = await generateStatements({
        storeCd: store,
        issueTerm,
        startDate,
        endDate,
        dealCode: isAdHoc ? adHocDealCode : undefined,
        geoEcode: isAdHoc ? adHocGeoEcode : undefined,
        oilType: isAdHoc ? adHocOilType : undefined,
        srcNos,
      });
      toast({
        title: '거래명세서 생성 완료',
        description: `거래처 ${result?.dealerCount ?? 0}건 · 라인 ${result?.lineCount ?? 0}건`,
      });
      await handleSearch();
    } catch (error) {
      fail('생성 실패', error);
    }
  }, [
    adHocDealCode,
    adHocGeoEcode,
    adHocOilType,
    endDate,
    fail,
    handleSearch,
    isAdHoc,
    issueTerm,
    startDate,
    storeCd,
    toast,
  ]);

  const handleCancel = useCallback(async (dealCodes: string[]) => {
    const store = requireStore();
    if (!store) return;
    try {
      const deleted = await cancelStatements({
        storeCd: store,
        issueYm: toIssueYm(startDate),
        dealCodes,
      });
      toast({ title: '생성 취소 완료', description: `${deleted}건 삭제` });
      await handleSearch();
    } catch (error) {
      // 동일 발행년월에 세금계산서가 있으면 서버가 전체를 차단한다
      fail('취소 실패', error);
    }
  }, [fail, handleSearch, startDate, storeCd, toast]);

  const handleGenerateTax = useCallback(async (dealCodes: string[]) => {
    const store = requireStore();
    if (!store) return;
    try {
      const { created = [], skipped = [] } = await generateTaxInvoices({
        storeCd: store,
        issueYm: toIssueYm(startDate),
        dealCodes,
      });
      // 발행할 수 없는 거래처가 있어도 나머지는 생성된다. 제외된 거래처를 알리지 않으면
      // 사용자가 "생성 완료"만 보고 빠진 거래처를 알아채지 못한다.
      toast({
        title: created.length > 0 ? '세금계산서 생성 완료' : '생성된 세금계산서가 없습니다',
        description:
          `${created.length}건 생성` +
          (skipped.length > 0
            ? ` · 제외 ${skipped.length}곳 (${skipped
                .map(item => `${item.dealCode} ${item.message ?? ''}`.trim())
                .join(', ')})`
            : ''),
      });
      await handleSearch();
    } catch (error) {
      fail('세금계산서 생성 실패', error);
    }
  }, [fail, handleSearch, startDate, storeCd, toast]);

  /** 거래명세서 인쇄 - 행의 명세서 View는 그 거래처만, 헤더 버튼은 체크한 거래처들 */
  const handlePreview = useCallback(async (dealCodes: string[]) => {
    const store = requireStore();
    if (!store) return;
    try {
      const response = await fetchInvoiceDocuments({
        storeCd: store,
        startDate,
        endDate,
        dealCodes: dealCodes.length > 0 ? dealCodes : undefined,
      });
      // 품목명·단위를 유종마스터로 해석하므로, 비어 있으면 유종코드가 그대로 인쇄된다
      const oils = getOils(store) as OilMaster[];
      if (oils.length === 0) {
        toast({
          title: '미리보기 실패',
          description: '유종 정보를 불러오지 못했습니다. 새로고침 후 다시 시도하세요',
          variant: 'destructive',
        });
        return;
      }
      const docs = (response.documents ?? []).map(document =>
        buildInvoiceDocument(document, response.supplier, oils)
      );
      if (docs.length === 0) {
        toast({ title: '출력할 거래명세서가 없습니다' });
        return;
      }
      setPreviewDocs(docs);
    } catch (error) {
      fail('미리보기 실패', error);
    }
  }, [endDate, fail, getOils, startDate, storeCd, toast]);

  /** 생성된 세금계산서 보기 (§4.2 계산서 View) */
  const handleTaxPreview = useCallback(
    async (taxNo: number) => {
      try {
        const doc = buildTaxInvoiceDocument(await fetchTaxInvoiceDocument(taxNo));
        setTaxPreviewDoc(doc);
        setTaxRemarkDraft(doc.remark);
      } catch (error) {
        fail('세금계산서 보기 실패', error);
      }
    },
    [fail]
  );

  /** 비고 저장 (§4.3). 수정세금계산서의 당초 승인번호 등을 문서 단위로 기록한다 */
  const handleSaveRemark = useCallback(async () => {
    if (!taxPreviewDoc) return;
    try {
      // 서버도 trim·빈값→NULL 정규화를 하므로 응답을 되읽지 않고 보낸 값으로 맞춘다
      const remark = taxRemarkDraft.trim();
      await updateTaxInvoiceRemark(taxPreviewDoc.taxNo, remark);
      setTaxPreviewDoc({ ...taxPreviewDoc, remark });
      setTaxRemarkDraft(remark);
      toast({ title: '비고 저장 완료' });
    } catch (error) {
      fail('비고 저장 실패', error);
    }
  }, [fail, taxPreviewDoc, taxRemarkDraft, toast]);

  /** 영수↔청구 토글 (§4.3). 합계금액이 현금↔외상 칸으로 옮겨간다 */
  const handleToggleReceipt = useCallback(
    async (taxNo: number) => {
      try {
        const view = await toggleReceiptPurpose(taxNo);
        toast({
          title: '영수·청구 변경 완료',
          description: view?.receiptPurposeCode === '01' ? '영수로 변경했습니다' : '청구로 변경했습니다',
        });
        // 계산서 View 안에서 누르므로 열린 문서를 새 값으로 다시 그린다
        setTaxPreviewDoc(buildTaxInvoiceDocument(await fetchTaxInvoiceDocument(taxNo)));
        await handleSearch();
      } catch (error) {
        fail('영수·청구 변경 실패', error);
      }
    },
    [fail, handleSearch, toast]
  );

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('invoice')} />
      }
      pageHeader={
        <PageHeader onSearch={handleSearch} isLoading={isLoading} />
      }
      title='거래명세서 발행'
      onExpandChange={handleExpandChange}
    >
      <div className='space-y-4'>
        {/* 검색조건(정본 search-grid). 거래처·확장코드·상품은 항상 보이되 수시(6)에서만 활성화된다 */}
        <div className='flex flex-wrap items-end gap-3'>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>발행기간유형</label>
            <Select
              value={issueTerm}
              onValueChange={value => {
                setIssueTerm(value);
                // 수시(6) 전용 조건이 disabled 필드에 남아 보이지 않도록 벗어날 때 지운다
                if (value !== ISSUE_TERM_AD_HOC) {
                  setAdHocDealCode('');
                  setAdHocGeoEcode('');
                  setAdHocOilType('');
                }
              }}
            >
              <SelectTrigger className='w-40'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ISSUE_TERM_LABEL).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>발행기간</label>
            <DateRangeInputs
              start={startDate}
              end={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </div>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>거래처</label>
            <Popover open={custOpen} onOpenChange={setCustOpen}>
              <PopoverTrigger asChild>
                <button
                  type='button'
                  role='combobox'
                  disabled={!isAdHoc}
                  className='flex h-10 w-56 items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50'
                >
                  <span className='truncate'>
                    {selectedCust ? custLabel(selectedCust) : '- 거래처 선택 -'}
                  </span>
                  <ChevronDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                </button>
              </PopoverTrigger>
              <PopoverContent className='w-56 p-0' align='start'>
                <Command
                  // 기본 스코어러는 퍼지 매칭이라 코드 "0003" 이 8003·9003 까지 잡는다. 부분일치로 고정한다.
                  filter={(value, search) => (value.includes(search.toLowerCase()) ? 1 : 0)}
                >
                  <CommandInput placeholder='코드 · 거래처명 검색' />
                  <CommandList>
                    <CommandEmpty>일치하는 거래처가 없습니다</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value='- 거래처 선택 -'
                        onSelect={() => {
                          setAdHocDealCode('');
                          setAdHocGeoEcode('');
                          setCustOpen(false);
                        }}
                      >
                        - 거래처 선택 -
                      </CommandItem>
                      {/* 정본: 발행주기 미설정(0) 거래처도 수시 발행 대상이라 목록에 두고 라벨로만 구분한다(PRD §6.1) */}
                      {sortedCust.map(d => (
                        <CommandItem
                          key={d.dealCode}
                          // cmdk 는 이 값으로 필터링한다. 코드·거래처명 둘 다로 검색되게 함께 넣는다.
                          value={`${d.dealCode} ${d.dealName}`}
                          onSelect={() => {
                            setAdHocDealCode(d.dealCode ?? '');
                            setAdHocGeoEcode('');
                            setCustOpen(false);
                          }}
                        >
                          {custLabel(d)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>확장코드</label>
            <Select
              value={adHocGeoEcode || NONE}
              onValueChange={value => setAdHocGeoEcode(value === NONE ? '' : value)}
              disabled={!isAdHoc || geoOptions.length === 0}
            >
              <SelectTrigger className='w-44'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>deal_code 기준(기본)</SelectItem>
                {geoOptions.map(geo => (
                  <SelectItem key={geo} value={geo}>
                    {geo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>상품</label>
            <Select
              value={adHocOilType || NONE}
              onValueChange={value => setAdHocOilType(value === NONE ? '' : value)}
              disabled={!isAdHoc}
            >
              <SelectTrigger className='w-40'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>전체</SelectItem>
                {(getOils(selectedStore) as OilMaster[]).map(oil => (
                  <SelectItem key={oil.oilCode} value={oil.oilCode ?? ''}>
                    {oil.oilName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {storeNotice && (
          <p className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900'>
            {storeNotice}
          </p>
        )}

        {/* 정본 §2.7.3 좌/우 2분할 — 좌: 거래내역(pos_mchul), 우: 거래명세서 내역(invoice).
            각 표가 절반 폭이라 패널 안에서 가로 스크롤이 생긴다(min-w 유지). */}
        <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
          <SalesPane
            rows={salesRows}
            onGenerate={srcNos => void handleGenerate(srcNos)}
            isLoading={isLoading}
            isExpanded={isExpanded}
          />
          <StatementPane
            rows={statements}
            isLoading={isLoading}
            isExpanded={isExpanded}
            onPrint={dealCodes => void handlePreview(dealCodes)}
            onCancel={dealCodes => void handleCancel(dealCodes)}
            onGenerateTax={dealCodes => void handleGenerateTax(dealCodes)}
            onViewTax={handleTaxPreview}
          />
        </div>

        {/* 정본 status-bar */}
        <div className='flex gap-6 text-xs text-muted-foreground'>
          <span>
            거래내역(미생성) <b>{salesRows.filter(r => !r.issued).length}</b>건
          </span>
          <span>
            확정 거래명세서 <b>{statements.filter(r => (r.lineCount ?? 0) > 0).length}</b>건
          </span>
          <span>
            발행 세금계산서 <b>{statements.reduce((n, r) => n + (r.taxNos?.length ?? 0), 0)}</b>건
          </span>
        </div>
      </div>

      {taxPreviewDoc && (
        <PrintPreviewOverlay
          title={`${taxPreviewDoc.titleText} ${taxPreviewDoc.approvalNo}`}
          onClose={() => setTaxPreviewDoc(null)}
          toolbarExtra={
            // 국세청 승인번호가 있는 문서(미등록문서·검증 완료)는 서버도 수정을 거부하므로 편집 UI를 빼고 안내만 보인다
            taxPreviewDoc.approvalNo ? (
              <span className='text-xs opacity-80'>국세청 확정 문서 - 비고·영수/청구 수정 불가</span>
            ) : (
              <span className='flex items-center gap-2'>
                <Input
                  value={taxRemarkDraft}
                  onChange={event => setTaxRemarkDraft(event.target.value)}
                  placeholder='비고 입력 (예: 당초 승인번호)'
                  maxLength={500}
                  className='h-7 w-80 bg-white text-black'
                />
                <button
                  type='button'
                  onClick={() => void handleSaveRemark()}
                  disabled={taxRemarkDraft === taxPreviewDoc.remark}
                >
                  비고 저장
                </button>
                {/* §4.3 계산서 View의 영수/청구 라벨 버튼 - 클릭마다 청구↔영수 전환 */}
                <button type='button' onClick={() => void handleToggleReceipt(taxPreviewDoc.taxNo)}>
                  영수·청구 전환
                </button>
              </span>
            )
          }
        >
          <TaxInvoiceDocument doc={taxPreviewDoc} />
        </PrintPreviewOverlay>
      )}

      {previewDocs && (
        <InvoicePreviewOverlay docs={previewDocs} onClose={() => setPreviewDocs(null)} />
      )}
    </ExpandableCard>
  );
}

/** 2분할되는 두 패널의 높이 */
function paneHeight(isExpanded: boolean) {
  return isExpanded ? 'h-[calc(100vh-360px)]' : 'h-[calc(100vh-420px)]';
}

function SalesPane({
  rows,
  onGenerate,
  isLoading,
  isExpanded,
}: {
  rows: SalesRow[];
  onGenerate: (srcNos: number[]) => void;
  isLoading: boolean;
  isExpanded: boolean;
}) {
  const { getOils } = useCommon();
  const selectedStore = useFilterStore(state => state.store[0] ?? '');
  const [checked, setChecked] = useState<Record<string, unknown>[]>([]);
  const syncChecked = useCallback((grid: RealGrid.GridView) => setChecked(checkedRows(grid)), []);

  // 판매자료의 유종은 코드(1004 등)라 유종마스터로 이름을 붙인다. 마스터에 없으면 코드를 그대로 둔다.
  const oils = useMemo(() => getOils(selectedStore) as OilMaster[], [getOils, selectedStore]);
  const gridRows = useMemo(
    () =>
      rows.map(row => ({
        ...row,
        oilName: oils.find(oil => oil.oilCode === row.oilType)?.oilName ?? row.oilType ?? '-',
      })),
    [oils, rows]
  );

  return (
    <div className='space-y-1'>
      <div className='flex items-center justify-between'>
        <div className='text-sm font-medium'>
          거래내역 <span className='text-xs font-normal text-muted-foreground'>(회색은 거래명세서 생성완료분)</span>
        </div>
        <Button
          size='sm'
          onClick={() => onGenerate(checked.map(r => r.srcNo as number))}
          disabled={isLoading || !checked.length}
        >
          거래명세서 생성
        </Button>
      </div>
      <div className={`border rounded-lg ${paneHeight(isExpanded)}`}>
        <RealGridReact
          gridProps={{
            ...GRID_BASE,
            checkBar: { visible: true, checkableCallback: (ds, item) => !ds.getValue(item.dataRow, 'issued') },
          }}
          rows={gridRows}
          // 조회 직후 미확정 행 전부가 기본 선택이다(정본 프로토타입). 확정된 행은 checkableCallback이 막는다
          onDataLoadComplated={grid => {
            grid.checkAll(true);
            syncChecked(grid);
          }}
          onItemChecked={syncChecked}
          onItemAllChecked={syncChecked}
          rowStyleCallback={(grid, item) =>
            grid.getDataSource().getValue(item.dataRow, 'issued') ? { style: { color: '#9ca3af' } } : undefined
          }
        >
          <RGDataField fieldName='srcNo' dataType={RealGrid.ValueType.NUMBER} />
          <RGDataField fieldName='issued' dataType={RealGrid.ValueType.BOOLEAN} />
          <RGDataField fieldName='sdate' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='dealName' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='oilName' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='carNo' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='qty' dataType={RealGrid.ValueType.NUMBER} />
          <RGDataField fieldName='unitPrice' dataType={RealGrid.ValueType.NUMBER} />
          <RGDataField fieldName='amount' dataType={RealGrid.ValueType.NUMBER} />
          <RGDataField fieldName='supplyAmt' dataType={RealGrid.ValueType.NUMBER} />
          <RGDataField fieldName='vatAmt' dataType={RealGrid.ValueType.NUMBER} />
          <RGDataField fieldName='invoiceDocNo' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='taxDocNo' dataType={RealGrid.ValueType.TEXT} />
          <RGDataColumn fieldName='sdate' header={{ text: '일자' }} width={90} />
          <RGDataColumn fieldName='dealName' header={{ text: '거래처' }} width={140} />
          <RGDataColumn fieldName='oilName' header={{ text: '유종' }} width={80} />
          <RGDataColumn fieldName='carNo' header={{ text: '차량번호' }} width={100} />
          <RGDataColumn fieldName='qty' header={{ text: '수량' }} width={80} numberFormat='#,##0.###' styleName='text-right' />
          <RGDataColumn fieldName='unitPrice' header={{ text: '단가' }} width={85} numberFormat='#,##0' styleName='text-right' />
          <RGDataColumn fieldName='amount' header={{ text: '금액' }} width={100} numberFormat='#,##0' styleName='text-right' />
          <RGDataColumn fieldName='supplyAmt' header={{ text: '공급가' }} width={95} numberFormat='#,##0' styleName='text-right' />
          <RGDataColumn fieldName='vatAmt' header={{ text: '부가세' }} width={85} numberFormat='#,##0' styleName='text-right' />
          <RGDataColumn fieldName='invoiceDocNo' header={{ text: '거래명세서No' }} width={130} />
          <RGDataColumn fieldName='taxDocNo' header={{ text: '세금계산서No' }} width={130} />
        </RealGridReact>
      </div>
    </div>
  );
}

function StatementPane({
  rows,
  isLoading,
  isExpanded,
  onPrint,
  onCancel,
  onGenerateTax,
  onViewTax,
}: {
  rows: StatementSummary[];
  isLoading: boolean;
  isExpanded: boolean;
  onPrint: (dealCodes: string[]) => void;
  onCancel: (dealCodes: string[]) => void;
  onGenerateTax: (dealCodes: string[]) => void;
  onViewTax: (taxNo: number) => void;
}) {
  const [checked, setChecked] = useState<Record<string, unknown>[]>([]);
  // 세금계산서가 여러 건인 거래처에서 어느 문서를 열지 고르는 중이면 그 거래처코드
  const [taxChoice, setTaxChoice] = useState<string | null>(null);

  // 버튼 셀은 값이 비면 숨는다(hideWhenEmpty) - 행별 활성 여부를 값으로 표현한다
  const gridRows = useMemo(
    () =>
      rows.map(row => {
        const taxNos = row.taxNos ?? [];
        const issued = taxNos.length > 0;
        // 생성 가능 여부는 문서 유무가 아니라 미채번 라인 유무다(PRD §4.8).
        // 필드가 없는 옛 api에서는 문서 유무로 되돌아간다(web이 api보다 먼저 배포돼도 버튼이 남게)
        const canGenerate = row.untaxedLineCount === undefined ? !issued : row.untaxedLineCount > 0;
        // 세금계산서만 있고 거래명세서 라인이 없는 거래처(C420 미등록문서 등록 등, §2.7.3) - 붉은 행, 체크 불가
        const taxOnly = (row.lineCount ?? 0) === 0 && issued;
        return {
          dealCode: row.dealCode,
          dealer: `${row.dealCode ?? ''} ${row.dealName ?? ''}`.trim(),
          lineCount: taxOnly ? null : row.lineCount,
          supplyAmtTotal: taxOnly ? null : row.supplyAmtTotal,
          vatAmtTotal: taxOnly ? null : row.vatAmtTotal,
          geoEcodes: (row.geoEcodes ?? []).join(', '),
          btnStatement: taxOnly ? '' : 'View',
          btnTaxGen: canGenerate ? '생성' : '',
          btnTaxView: issued ? 'View' : '',
          docNos: (row.docNos ?? []).join(', '),
          taxDocNos: (row.taxDocNos ?? []).join(', '),
          hasTax: issued,
          taxOnly,
        };
      }),
    [rows]
  );

  useEffect(() => setChecked([]), [gridRows]);
  const syncChecked = useCallback((grid: RealGrid.GridView) => setChecked(checkedRows(grid)), []);

  const dealCodes = checked.map(r => r.dealCode as string);
  // 정본: 체크한 거래처 중 세금계산서가 있는 곳이 있으면 취소 버튼을 미리 막는다(가드와 같은 조건)
  const selectedHasTax = checked.some(r => r.hasTax);

  return (
    <div className='space-y-1'>
      <div className='flex items-center justify-between'>
        <div className='text-sm font-medium'>
          거래명세서 내역 <span className='text-xs font-normal text-muted-foreground'>(기 생성분)</span>
        </div>
        <div className='flex gap-1'>
          <Button size='sm' variant='outline' onClick={() => onPrint(dealCodes)} disabled={isLoading || !dealCodes.length}>
            거래명세서 인쇄
          </Button>
          <Button
            size='sm'
            variant='outline'
            onClick={() => onCancel(dealCodes)}
            disabled={isLoading || !dealCodes.length || selectedHasTax}
          >
            거래명세서 생성 취소
          </Button>
          <Button size='sm' onClick={() => onGenerateTax(dealCodes)} disabled={isLoading || !dealCodes.length}>
            세금계산서 생성
          </Button>
        </div>
      </div>
      <div className={`border rounded-lg ${paneHeight(isExpanded)}`}>
        <RealGridReact
          gridProps={{
            ...GRID_BASE,
            // orphan(세금계산서만 있는) 행은 체크 불가 → 일괄선택에서 자동 제외(§2.7.3)
            checkBar: { visible: true, checkableCallback: (ds, item) => !ds.getValue(item.dataRow, 'taxOnly') },
          }}
          rows={gridRows}
          onItemChecked={syncChecked}
          onItemAllChecked={syncChecked}
          rowStyleCallback={(grid, item) =>
            grid.getDataSource().getValue(item.dataRow, 'taxOnly')
              ? { style: { backgroundColor: '#fdecea', color: '#c0392b' } }
              : undefined
          }
          // renderer:{type:'button'} 클릭은 onCellButtonClicked(button:'action' 전용)가 아니라 onCellItemClicked로 온다
          onCellItemClicked={(grid, index) => {
            const row = grid.getDataSource().getJsonRow(grid.getDataRow(index.itemIndex ?? -1)) as Record<string, unknown>;
            const dealCode = row.dealCode as string;
            const field = index.fieldName;
            if (field === 'btnStatement') onPrint([dealCode]);
            else if (field === 'btnTaxGen') onGenerateTax([dealCode]);
            else if (field === 'btnTaxView') {
              // 계산서인쇄유형2 2·3 은 거래처당 세금계산서가 여러 건이다(§4.10.2).
              // 1건이면 바로 열고, 여러 건이면 어느 문서를 볼지 고르게 한다.
              const taxNos = rows.find(r => r.dealCode === dealCode)?.taxNos ?? [];
              if (taxNos.length === 1) onViewTax(taxNos[0]);
              else if (taxNos.length > 1) setTaxChoice(dealCode);
            }
            return true;
          }}
        >
          <RGDataField fieldName='dealCode' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='dealer' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='lineCount' dataType={RealGrid.ValueType.NUMBER} />
          <RGDataField fieldName='supplyAmtTotal' dataType={RealGrid.ValueType.NUMBER} />
          <RGDataField fieldName='vatAmtTotal' dataType={RealGrid.ValueType.NUMBER} />
          <RGDataField fieldName='geoEcodes' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='btnStatement' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='btnTaxGen' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='btnTaxView' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='docNos' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='taxDocNos' dataType={RealGrid.ValueType.TEXT} />
          <RGDataField fieldName='hasTax' dataType={RealGrid.ValueType.BOOLEAN} />
          <RGDataField fieldName='taxOnly' dataType={RealGrid.ValueType.BOOLEAN} />
          <RGDataColumn fieldName='dealer' header={{ text: '거래처' }} width={190} />
          <RGDataColumn fieldName='lineCount' header={{ text: '건수' }} width={60} numberFormat='#,##0' styleName='text-right' />
          <RGDataColumn fieldName='supplyAmtTotal' header={{ text: '공급가합계' }} width={110} numberFormat='#,##0' styleName='text-right' />
          <RGDataColumn fieldName='vatAmtTotal' header={{ text: '부가세합계' }} width={100} numberFormat='#,##0' styleName='text-right' />
          <RGDataColumn fieldName='geoEcodes' header={{ text: '확장코드' }} width={100} />
          <RGDataColumn fieldName='btnStatement' header={{ text: '명세서' }} width={70} renderer={{ type: 'button', hideWhenEmpty: true }} />
          <RGDataColumn fieldName='btnTaxGen' header={{ text: '계산서' }} width={70} renderer={{ type: 'button', hideWhenEmpty: true }} />
          <RGDataColumn fieldName='btnTaxView' header={{ text: '계산서' }} width={70} renderer={{ type: 'button', hideWhenEmpty: true }} />
          <RGDataColumn fieldName='docNos' header={{ text: '거래명세서No' }} width={140} />
          <RGDataColumn fieldName='taxDocNos' header={{ text: '세금계산서No' }} width={150} />
        </RealGridReact>
      </div>

      {/* 세금계산서가 여러 건인 거래처는 어느 문서를 볼지 고른다 - taxNos 는 taxDocNos 와 같은 순서다 */}
      <Dialog open={taxChoice !== null} onOpenChange={open => !open && setTaxChoice(null)}>
        <DialogContent className='sm:max-w-sm'>
          <DialogHeader>
            <DialogTitle>세금계산서 선택</DialogTitle>
          </DialogHeader>
          <div className='flex flex-col gap-2'>
            {(() => {
              const row = rows.find(r => r.dealCode === taxChoice);
              return (row?.taxDocNos ?? []).map((docNo, i) => (
                <Button
                  key={docNo}
                  variant='outline'
                  className='justify-start'
                  onClick={() => {
                    const taxNo = row?.taxNos?.[i];
                    setTaxChoice(null);
                    if (taxNo != null) onViewTax(taxNo);
                  }}
                >
                  {docNo}
                </Button>
              ));
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
